import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy } from 'react';

// Layout
import Layout from './components/Layout';
import GbrFrame, { NotFound } from './components/gbr/GbrFrame';
import PrivateRoute from './components/PrivateRoute';
import GuestRoute from './components/GuestRoute';
import LoadingSpinner from './components/LoadingSpinner';
import { ThemeSync } from './lib/theme';
import { DesignUpdateStrip } from './components/gbr/DesignUpdate';
import { ConsentBanner } from './components/gbr/ConsentBanner';

// Eager loaded pages (wichtig für SEO)
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';

// Lazy loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Library = lazy(() => import('./pages/Library'));
const AddBook = lazy(() => import('./pages/AddBook'));
const EditBook = lazy(() => import('./pages/EditBook'));
const MyBooks = lazy(() => import('./pages/MyBooks'));
const BookDetail = lazy(() => import('./pages/BookDetail'));
const Reviewing = lazy(() => import('./pages/Reviewing'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const Advertise = lazy(() => import('./pages/Advertise'));
const PackageOrder = lazy(() => import('./pages/PackageOrder'));
const PackagesAdmin = lazy(() => import('./pages/PackagesAdmin'));
const FeatureArticle = lazy(() => import('./pages/FeatureArticle'));
function FeatureRedirect() {
  const loc = useLocation();
  return <Navigate to={'/packages' + loc.search} replace />;
}
const FeatureOrder = lazy(() => import('./pages/FeatureOrder'));
const Settings = lazy(() => import('./pages/Settings'));
const Admin = lazy(() => import('./pages/Admin'));
function Admin2Redirect() {
  const loc = useLocation();
  return <Navigate to={loc.pathname.replace(/^\/admin2/, '/admin') + loc.search} replace />;
}
const Admin2 = lazy(() => import('./pages/admin2/Admin2'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ResendVerification = lazy(() => import('./pages/ResendVerification'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const FAQ = lazy(() => import('./pages/FAQ'));
const FreeBooks = lazy(() => import('./pages/FreeBooks'));
const Contact = lazy(() => import('./pages/Contact'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

function App() {
  return (
    <>
    <ThemeSync />
    <DesignUpdateStrip />
    <ConsentBanner />
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/book/:id" element={<BookDetail />} />
        <Route path="/library" element={<Library />} />
        <Route element={<GbrFrame />}>
        {/* Public Routes */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/resend-verification" element={<ResendVerification />} />

        {/* Guest Only Routes */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <Login />
            </GuestRoute>
          }
        />
        <Route
          path="/register"
          element={
            <GuestRoute>
              <Register />
            </GuestRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestRoute>
              <ForgotPassword />
            </GuestRoute>
          }
        />
        <Route
          path="/reset-password/:token"
          element={
            <GuestRoute>
              <ResetPassword />
            </GuestRoute>
          }
        />

        {/* Public Routes (accessible without login) - must come BEFORE protected routes */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/help" element={<FAQ />} />
        <Route path="/free-books-to-review" element={<FreeBooks />} />
        <Route path="/advertise" element={<Advertise />} />

          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Protected Routes - only for features that REQUIRE login */}
        <Route element={<PrivateRoute />}>
          <Route path="/admin2/*" element={<Admin2Redirect />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/reviewing" element={<Reviewing />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/my-books" element={<MyBooks />} />
          <Route element={<GbrFrame pad />}>
            <Route path="/admin/*" element={<Admin2 />} />
            <Route path="/add-book" element={<AddBook />} />
            <Route path="/book/:id/edit" element={<EditBook />} />
            <Route path="/affiliate" element={<Affiliate />} />
            <Route path="/packages" element={<FeatureArticle />} />
            <Route path="/packages/order/:id" element={<PackageOrder />} />
            <Route path="/packages-admin" element={<PackagesAdmin />} />
            <Route path="/feature-article" element={<FeatureRedirect />} />
            <Route path="/feature-article/order/:id" element={<FeatureOrder />} />
          </Route>
          <Route element={<Layout />}>
            <Route path="/admin-old" element={<Admin />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
    </>
  );
}

export default App;
